import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { QueryParams, ApiResponse } from './types';
import { CsrfTokenManager } from './CSrfTokenManager';

interface ApiErrorResponse {
    message?: string;
    error?: string;
    statusCode?: number;
  }

export class ApiClient {
  private axiosInstance: AxiosInstance;
  private csrfTokenManager: CsrfTokenManager;

  constructor(baseURL: string) {
    this.csrfTokenManager = CsrfTokenManager.getInstance();
    const token = this.csrfTokenManager.getToken();
    
    this.axiosInstance = axios.create({
        baseURL,
        timeout: 30000,
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'x-xsrf-token': token 
        }
    });

    this.setupInterceptors();
}

  private setupInterceptors(): void {
      this.axiosInstance.interceptors.request.use(
          (config) => {
              if (!config.headers) {
                  config.headers = {};
              }
              // Get current token for each request
              config.headers['x-xsrf-token'] = this.csrfTokenManager.getToken();
              return config;
          },
          (error) => Promise.reject(error)
      );
  }

  public async get<T>(
    endpoint: string,
    queryParams?: any
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.get(endpoint, {
        params: queryParams
      });

      return {
        data: response.data,
        status: response.status,
        message: response.statusText
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async post<T>(
    endpoint: string,
    data: any,
    queryParams?: any
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.post(endpoint, data, {
        params: queryParams
      });

      return {
        data: response.data,
        status: response.status,
        message: response.statusText
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }


  private handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      const errorData = (error.response?.data as ApiErrorResponse) || {};
      
      const errorMessage = 
        errorData.message || 
        errorData.error || 
        error.message || 
        'An unknown error occurred';

      return new Error(`API Error: ${errorMessage}`);
    }
    return new Error('An unexpected error occurred');
  }
}